import { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Square, RotateCcw, Camera, Maximize2, Minimize2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Background definitions ────────────────────────────────────────────────────
interface BgOption {
  key: string;
  label: string;
  /** none = raw stream, blur = ML cutout + blurred background, color = ML cutout + solid fill, image = ML cutout + image */
  type: 'none' | 'blur' | 'color' | 'image';
  blurAmount?: number;
  color?: string;
  src?: string;
  /** CSS background value used for the thumbnail swatch */
  thumb: string;
}

const BG_OPTIONS: BgOption[] = [
  {
    key: 'none',
    label: 'Off',
    type: 'none',
    thumb: 'repeating-conic-gradient(#d1d5db 0% 25%,#f3f4f6 0% 50%) 0 0/10px 10px',
  },
  {
    key: 'blur',
    label: 'Blur',
    type: 'blur',
    blurAmount: 16,
    thumb: 'linear-gradient(135deg,#94a3b8,#cbd5e1)',
  },
];

// ─── MediaPipe loader ──────────────────────────────────────────────────────────
declare global {
  interface Window { SelfieSegmentation: any; }
}

let mediaPipeLoading: Promise<void> | null = null;

async function ensureMediaPipe(): Promise<void> {
  if (window.SelfieSegmentation) return;
  if (mediaPipeLoading) return mediaPipeLoading;
  mediaPipeLoading = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1/selfie_segmentation.js';
    s.crossOrigin = 'anonymous';
    s.onload = () => resolve();
    s.onerror = () => { mediaPipeLoading = null; reject(new Error('MediaPipe load failed')); };
    document.head.appendChild(s);
  });
  return mediaPipeLoading;
}

// ─── Component ─────────────────────────────────────────────────────────────────
interface VideoRecorderProps {
  maxDurationSeconds?: number;
  onRecordingComplete: (blob: Blob) => void;
  className?: string;
  teleprompterScript?: string;
}

type RecorderState = 'idle' | 'requesting' | 'previewing' | 'recording' | 'reviewing';

export function VideoRecorder({
  maxDurationSeconds = 40,
  onRecordingComplete,
  className,
  teleprompterScript,
}: VideoRecorderProps) {
  // ── Existing state ──────────────────────────────────────────────────────────
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [teleprompterOn, setTeleprompterOn] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scrollSpeed, setScrollSpeed] = useState<'slow' | 'normal' | 'fast'>('slow');
  const [scriptDismissed, setScriptDismissed] = useState(false);

  // ── Background state ────────────────────────────────────────────────────────
  const [selectedBgKey, setSelectedBgKey] = useState('none');
  const [segLoading, setSegLoading] = useState(false);

  // ── Existing refs ───────────────────────────────────────────────────────────
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const previewRef = useRef<HTMLVideoElement>(null);
  const reviewRef = useRef<HTMLVideoElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Background processing refs ──────────────────────────────────────────────
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Offscreen canvases — not in DOM
  const maskCanvas = useRef<HTMLCanvasElement>(
    typeof document !== 'undefined' ? document.createElement('canvas') : null as any
  );
  // featherCanvas: holds the blurred mask for smooth edge blending
  const featherCanvas = useRef<HTMLCanvasElement>(
    typeof document !== 'undefined' ? document.createElement('canvas') : null as any
  );
  // edgeCanvas: quarter-size canvas for R→A conversion
  const edgeCanvas = useRef<HTMLCanvasElement>(
    typeof document !== 'undefined' ? document.createElement('canvas') : null as any
  );
  // prevMaskCanvas: stores the previous frame's alpha mask for temporal smoothing
  const prevMaskCanvas = useRef<HTMLCanvasElement>(
    typeof document !== 'undefined' ? document.createElement('canvas') : null as any
  );
  const animFrameRef = useRef<number | null>(null);
  const segmenterRef = useRef<any>(null);
  const bgImageRef = useRef<HTMLImageElement | null>(null);
  // Keep bg option in a ref so render loop callbacks always read the latest value
  const selectedBgRef = useRef<BgOption>(BG_OPTIONS[0]);

  // Sync ref on state change
  useEffect(() => {
    selectedBgRef.current = BG_OPTIONS.find(b => b.key === selectedBgKey) ?? BG_OPTIONS[0];
  }, [selectedBgKey]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const stopRenderLoop = useCallback(() => {
    if (animFrameRef.current != null) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    stopRenderLoop();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, [stopRenderLoop]);

  const syncCanvasSize = useCallback(() => {
    const video = previewRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth  || 1280;
    const h = video.videoHeight || 720;
    canvas.width = w;
    canvas.height = h;
    maskCanvas.current.width = w;
    maskCanvas.current.height = h;
    featherCanvas.current.width = w;
    featherCanvas.current.height = h;
    edgeCanvas.current.width = w;
    edgeCanvas.current.height = h;
    prevMaskCanvas.current.width = w;
    prevMaskCanvas.current.height = h;
  }, []);

  // ── Segmentation results → composite onto canvas ────────────────────────────
  //
  // Root cause of bad edges: MediaPipe SelfieSegmentation (v0.1) encodes the
  // mask in the RED channel (R=255 = person, R=0 = background). Alpha is always
  // 255. Canvas `destination-in` compositing reads the SOURCE ALPHA — so if we
  // pass the raw mask, destination-in sees alpha=255 everywhere and cuts nothing.
  //
  // Fix:
  //   1. Read the mask at 1/4 resolution via getImageData (fast — 320×180 px).
  //   2. Convert R channel → A channel so destination-in works correctly.
  //   3. Write back and scale up to full size (browser bilinear interpolation
  //      gives us free edge smoothing as it upscales).
  //   4. Apply an additional blur on the full-size alpha mask for feathering.
  //   5. Cut person from frame using destination-in (now correct).
  //   6. Composite person over background cleanly — no transition layers.
  //
  const onSegResults = useCallback((results: any) => {
    const canvas  = canvasRef.current;
    const mask    = maskCanvas.current;
    const feather = featherCanvas.current;
    const edge    = edgeCanvas.current;
    if (!canvas || !mask || !feather || !edge) return;

    const prev  = prevMaskCanvas.current;
    const ctx   = canvas.getContext('2d')!;
    const mCtx  = mask.getContext('2d')!;
    const fCtx  = feather.getContext('2d')!;
    const eCtx  = edge.getContext('2d', { willReadFrequently: true })!;
    const pCtx  = prev.getContext('2d', { willReadFrequently: true })!;
    const W = canvas.width, H = canvas.height;
    const bg = selectedBgRef.current;

    // ── Step 1: read raw mask at 1/4 size, convert R → alpha ─────────────────
    // MediaPipe encodes mask confidence in the RED channel (R=255=person).
    // Alpha is always 255. We must convert R→A before destination-in works.
    const QW = Math.ceil(W / 4), QH = Math.ceil(H / 4);
    if (edge.width !== QW || edge.height !== QH) {
      edge.width = QW; edge.height = QH;
    }
    eCtx.clearRect(0, 0, QW, QH);
    eCtx.drawImage(results.segmentationMask, 0, 0, QW, QH);
    const cur = eCtx.getImageData(0, 0, QW, QH);

    // ── Step 2: temporal smoothing — blend with previous frame mask ───────────
    // Blending 60% current + 40% previous stabilises edge confidence values
    // so they don't oscillate each frame, eliminating colour flicker on hard
    // backgrounds (solid colour / image). The ratio is: more current = more
    // responsive; more previous = smoother but slower to update.
    const SMOOTH = 0.40; // weight of previous frame
    if (prev.width === QW && prev.height === QH) {
      const prevData = pCtx.getImageData(0, 0, QW, QH);
      const cd = cur.data, pd = prevData.data;
      for (let i = 0; i < cd.length; i += 4) {
        const blended = Math.round(cd[i] * (1 - SMOOTH) + pd[i] * SMOOTH);
        cd[i + 3] = blended; // A ← blended confidence
        cd[i] = cd[i + 1] = cd[i + 2] = 255;
      }
    } else {
      // First frame — no previous mask yet, just convert R→A
      const d = cur.data;
      for (let i = 0; i < d.length; i += 4) {
        d[i + 3] = d[i];
        d[i] = d[i + 1] = d[i + 2] = 255;
      }
    }
    eCtx.putImageData(cur, 0, 0);

    // Save current (pre-blur) as next frame's previous
    if (prev.width !== QW || prev.height !== QH) {
      prev.width = QW; prev.height = QH;
    }
    pCtx.clearRect(0, 0, QW, QH);
    // Save the raw R values (before we overwrote with 255) — re-read from segmentationMask
    pCtx.drawImage(results.segmentationMask, 0, 0, QW, QH);

    // ── Step 3: scale back to full size + feather blur ────────────────────────
    // Bilinear upscaling from 1/4 size gives free edge smoothing.
    // blur(8px) adds a wider feather for soft alpha falloff.
    fCtx.clearRect(0, 0, W, H);
    fCtx.filter = 'blur(8px)';
    fCtx.drawImage(edge, 0, 0, W, H);
    fCtx.filter = 'none';

    // ── Step 4: cut person out using alpha-correct feathered mask ─────────────
    mCtx.clearRect(0, 0, W, H);
    mCtx.drawImage(results.image, 0, 0, W, H);
    mCtx.globalCompositeOperation = 'destination-in';
    mCtx.drawImage(feather, 0, 0);
    mCtx.globalCompositeOperation = 'source-over';

    // ── Step 5: render background + person ───────────────────────────────────
    ctx.clearRect(0, 0, W, H);
    if (bg.type === 'blur') {
      ctx.filter = `blur(${bg.blurAmount ?? 18}px)`;
      ctx.drawImage(results.image, 0, 0, W, H);
      ctx.filter = 'none';
    } else if (bg.type === 'color' && bg.color) {
      ctx.fillStyle = bg.color;
      ctx.fillRect(0, 0, W, H);
    } else if (bg.type === 'image' && bgImageRef.current) {
      ctx.drawImage(bgImageRef.current, 0, 0, W, H);
    } else {
      ctx.fillStyle = '#111827';
      ctx.fillRect(0, 0, W, H);
    }
    ctx.drawImage(mask, 0, 0);
  }, []);

  // ── Segmentation render loop ────────────────────────────────────────────────
  const startSegLoop = useCallback(() => {
    const loop = () => {
      const video = previewRef.current;
      const seg = segmenterRef.current;
      if (video && seg && video.readyState >= 2) {
        seg.send({ image: video }).catch(() => {});
      }
      animFrameRef.current = requestAnimationFrame(loop);
    };
    animFrameRef.current = requestAnimationFrame(loop);
  }, []);

  // ── Load MediaPipe + init segmenter ─────────────────────────────────────────
  const initSegmenter = useCallback(async (): Promise<boolean> => {
    if (segmenterRef.current) return true;
    setSegLoading(true);
    try {
      await ensureMediaPipe();
      const seg = new window.SelfieSegmentation({
        locateFile: (f: string) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1/${f}`,
      });
      seg.setOptions({ modelSelection: 1 }); // 1 = landscape (higher quality)
      seg.onResults(onSegResults);
      await seg.initialize();
      segmenterRef.current = seg;
      return true;
    } catch {
      setError('Could not load background AI model. Try a solid colour background instead.');
      return false;
    } finally {
      setSegLoading(false);
    }
  }, [onSegResults]);

  // ── Pre-load background image ───────────────────────────────────────────────
  const preloadBgImage = useCallback((src: string) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload  = () => { bgImageRef.current = img; };
    img.onerror = () => { bgImageRef.current = null; };
    img.src = src;
  }, []);

  // ── Select background ───────────────────────────────────────────────────────
  const handleSelectBg = useCallback(async (bg: BgOption) => {
    stopRenderLoop();
    setSelectedBgKey(bg.key);
    selectedBgRef.current = bg;

    if (bg.type === 'none') return;

    // Pre-load image asset
    if (bg.type === 'image' && bg.src) preloadBgImage(bg.src);

    // Only run loops when camera is active
    if (state !== 'previewing' && state !== 'recording') return;

    syncCanvasSize();

    // All non-none modes use ML segmentation (blur just uses a blurred background layer)
    const ok = await initSegmenter();
    if (ok) startSegLoop();
  }, [state, stopRenderLoop, preloadBgImage, syncCanvasSize, initSegmenter, startSegLoop]);

  // ── Start camera ────────────────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    setError(null);
    setState('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      streamRef.current = stream;
      setState('previewing');

      requestAnimationFrame(() => {
        if (previewRef.current) {
          previewRef.current.srcObject = stream;
          previewRef.current.play().catch(() => {});
        }
      });

      // Re-start processing loop if a bg was already selected
      const bg = selectedBgRef.current;
      if (bg.type !== 'none') {
        setTimeout(() => {
          syncCanvasSize();
          if (segmenterRef.current) {
            startSegLoop();
          } else {
            initSegmenter().then(ok => { if (ok) startSegLoop(); });
          }
        }, 400);
      }
    } catch (err) {
      setState('idle');
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('Camera access denied. Please allow camera access in your browser settings.');
      } else {
        setError('Could not access camera. Please check your device.');
      }
    }
  }, [syncCanvasSize, initSegmenter, startSegLoop]);

  // ── Start recording ─────────────────────────────────────────────────────────
  const startRecording = useCallback(() => {
    if (!streamRef.current) return;
    const bg = selectedBgRef.current;
    const canvas = canvasRef.current;

    // When a background is active, record from canvas (video) + mic (audio)
    let recordingStream: MediaStream;
    if (bg.type !== 'none' && canvas) {
      const canvasStream = canvas.captureStream(30);
      recordingStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...streamRef.current.getAudioTracks(),
      ]);
    } else {
      recordingStream = streamRef.current;
    }

    chunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : MediaRecorder.isTypeSupported('video/webm')
        ? 'video/webm'
        : 'video/mp4';

    const recorder = new MediaRecorder(recordingStream, {
      mimeType,
      videoBitsPerSecond: 2_500_000,
    });

    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      setRecordedUrl(URL.createObjectURL(blob));
      setState('reviewing');
      stopStream();
      onRecordingComplete(blob);
    };
    recorder.onerror = () => { setError('Recording failed. Please try again.'); setState('previewing'); };

    recorderRef.current = recorder;
    recorder.start(1000);
    setElapsed(0);
    setState('recording');

    timerRef.current = setInterval(() => {
      setElapsed(prev => {
        const next = prev + 1;
        if (next >= maxDurationSeconds) {
          recorder.stop();
          if (timerRef.current) clearInterval(timerRef.current);
        }
        return next;
      });
    }, 1000);
  }, [maxDurationSeconds, onRecordingComplete, stopStream]);

  // ── Stop recording ──────────────────────────────────────────────────────────
  const stopRecording = useCallback(() => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  // ── Re-record ───────────────────────────────────────────────────────────────
  const handleReRecord = useCallback(() => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedUrl(null);
    setElapsed(0);
    setState('idle');
  }, [recordedUrl]);

  // ── Cleanup on unmount ──────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      stopStream();
      if (timerRef.current) clearInterval(timerRef.current);
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
      if (segmenterRef.current) { try { segmenterRef.current.close(); } catch { /* */ } }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fullscreen ──────────────────────────────────────────────────────────────
  const reattachStream = useCallback(() => {
    const attach = () => {
      if (previewRef.current && streamRef.current) {
        previewRef.current.srcObject = streamRef.current;
        previewRef.current.play().catch(() => {});
      }
    };
    attach();
    setTimeout(attach, 200);
  }, []);

  useEffect(() => {
    const handler = () => { setIsFullscreen(!!document.fullscreenElement); reattachStream(); };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, [reattachStream]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen().then(reattachStream).catch(() => {});
    } else {
      document.exitFullscreen().then(reattachStream).catch(() => {});
    }
  }, [reattachStream]);

  // Reattach stream after state transitions
  useEffect(() => {
    if ((state === 'previewing' || state === 'recording') && streamRef.current) {
      const attach = () => {
        if (previewRef.current && streamRef.current) {
          previewRef.current.srcObject = streamRef.current;
          previewRef.current.play().catch(() => {});
        }
      };
      attach();
      const t = setTimeout(attach, 150);
      return () => clearTimeout(t);
    }
  }, [state]);

  // ── Utilities ───────────────────────────────────────────────────────────────
  const formatTime = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const hasScript   = !!teleprompterScript?.trim();
  const activeBg    = BG_OPTIONS.find(b => b.key === selectedBgKey) ?? BG_OPTIONS[0];
  const bgActive    = activeBg.type !== 'none';
  const needsML     = activeBg.type !== 'none';

  // ── Background picker strip ─────────────────────────────────────────────────
  const BgPicker = () => (
    <div className="bg-slate-900 px-3 py-2.5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider">
          Virtual Background
        </p>
        {segLoading && (
          <span className="flex items-center gap-1 text-[10px] text-white/50">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading AI model…
          </span>
        )}
        {needsML && !segLoading && !segmenterRef.current && (
          <span className="text-[10px] text-amber-400/80">
            First use loads AI model (~3 MB)
          </span>
        )}
      </div>
      <div className="flex items-end gap-2 overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {BG_OPTIONS.map(bg => (
          <button
            key={bg.key}
            type="button"
            title={bg.label}
            onClick={() => handleSelectBg(bg)}
            className="flex-shrink-0 flex flex-col items-center gap-1 group"
          >
            <div
              className={cn(
                'w-10 h-10 rounded-lg border-2 transition-all duration-150 flex items-center justify-center overflow-hidden',
                selectedBgKey === bg.key
                  ? 'border-white scale-110 shadow-lg shadow-black/40'
                  : 'border-white/20 hover:border-white/50 hover:scale-105',
              )}
              style={{ background: bg.thumb }}
            >
              {bg.type === 'blur' && (
                <span className="text-[11px] font-bold text-white/60 blur-[2px] select-none">Aa</span>
              )}
            </div>
            <span className={cn(
              'text-[9px] font-medium leading-none transition-colors',
              selectedBgKey === bg.key ? 'text-white' : 'text-white/40 group-hover:text-white/70',
            )}>
              {bg.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  // ── Error ───────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className={className}>
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center space-y-3">
          <Camera className="w-8 h-8 mx-auto text-destructive/60" />
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={() => { setError(null); startCamera(); }}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  // ── Idle ────────────────────────────────────────────────────────────────────
  if (state === 'idle') {
    return (
      <div className={className}>
        <div
          onClick={startCamera}
          className="group relative border-2 border-dashed border-border hover:border-primary/50 rounded-xl bg-muted/20 hover:bg-primary/[0.03] transition-all duration-200 cursor-pointer p-10 text-center"
        >
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/15 transition-colors">
            <Camera className="w-5 h-5 text-primary" />
          </div>
          <p className="text-sm font-semibold text-foreground">Click to start camera</p>
          <p className="text-xs text-muted-foreground mt-1">Record up to {maxDurationSeconds} seconds</p>
        </div>
      </div>
    );
  }

  // ── Requesting ──────────────────────────────────────────────────────────────
  if (state === 'requesting') {
    return (
      <div className={className}>
        <div className="rounded-lg bg-muted/30 p-5 text-center">
          <div className="animate-pulse">
            <Camera className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Requesting camera access…</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Previewing + Recording — single persistent camera view ─────────────────
  // IMPORTANT: previewing and recording share one JSX block so React never
  // unmounts the <video> or <canvas> elements between states. Unmounting them
  // caused a blank frame on the canvas the moment Record was clicked, which
  // showed as a flash of fully-blurred background (no person) for ~150ms.
  if (state === 'previewing' || state === 'recording') {
    const isRec = state === 'recording';
    const progress = isRec ? (elapsed / maxDurationSeconds) * 100 : 0;
    const speedMult = scrollSpeed === 'slow' ? 1.5 : scrollSpeed === 'fast' ? 0.6 : 1;
    const scrollDuration = Math.round(maxDurationSeconds * 0.8 * speedMult);
    // Split script on newlines so each paragraph scrolls separately
    const scriptParas = (teleprompterScript ?? '').split('\n\n').filter(Boolean);

    return (
      <div className={className}>
        <style>{`
          @keyframes teleprompter-scroll {
            0%   { transform: translateY(0); }
            8%   { transform: translateY(0); }
            92%  { transform: translateY(-100%); }
            100% { transform: translateY(-100%); }
          }
        `}</style>
        <div
          ref={containerRef}
          className="rounded-xl overflow-hidden border border-border mx-auto bg-black"
          style={{ maxWidth: 860 }}
        >
          {/* ── Camera view — persistent across previewing/recording ── */}
          <div className="relative aspect-video bg-black">
            {/* Raw camera — always present; hidden only when bg compositing is active */}
            <video
              ref={previewRef}
              autoPlay muted playsInline
              className={cn('absolute inset-0 w-full h-full object-cover', bgActive ? 'invisible' : '')}
            />
            {/* Composited canvas */}
            <canvas
              ref={canvasRef}
              className={cn('absolute inset-0 w-full h-full object-cover', !bgActive ? 'invisible' : '')}
            />

            {/* Fullscreen toggle — always visible */}
            <button
              type="button" onClick={toggleFullscreen}
              className="absolute top-3 left-3 z-20 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center text-white/80 hover:text-white hover:bg-black/70"
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>

            {/* REC badge — only while recording */}
            {isRec && (
              <div className="absolute top-3 left-12 flex items-center gap-1.5 bg-red-600 text-white px-2 py-1 rounded-full text-xs font-medium z-10">
                <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                REC
              </div>
            )}

            {/* Timer — only while recording */}
            {isRec && (
              <div className="absolute top-3 right-3 bg-black/60 text-white px-2 py-1 rounded text-xs font-mono z-10">
                {formatTime(elapsed)} / {formatTime(maxDurationSeconds)}
              </div>
            )}

            {/* Teleprompter overlay — only while recording */}
            {isRec && hasScript && teleprompterOn && !scriptDismissed && (
              <div className="absolute bottom-10 left-0 right-0 z-10" style={{ height: '42%' }}>
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/70 to-black/80 pointer-events-none" />
                <div className="absolute inset-0 overflow-hidden px-4 pt-6">
                  <div
                    ref={scrollRef}
                    style={{
                      animation: `teleprompter-scroll ${scrollDuration}s linear forwards`,
                      animationPlayState: 'running',
                    }}
                  >
                    {scriptParas.map((para, i) => (
                      <p key={i} className="text-white text-base font-medium leading-relaxed drop-shadow-lg mb-4">
                        {para}
                      </p>
                    ))}
                    <div style={{ height: 80 }} />
                  </div>
                </div>
              </div>
            )}

            {/* Bottom bar */}
            <div className="absolute bottom-0 left-0 right-0 rounded-b-xl overflow-hidden z-20">
              {isRec && <Progress value={progress} className="h-1 rounded-none" />}
              <div className="p-3 bg-gradient-to-t from-black/60 to-transparent flex items-center justify-between">
                {!isRec && (
                  <Button
                    type="button" size="sm" variant="ghost"
                    className="text-white/80 hover:text-white hover:bg-white/10"
                    onClick={() => { stopStream(); setState('idle'); }}
                  >
                    Cancel
                  </Button>
                )}
                {isRec && hasScript ? (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setTeleprompterOn(v => !v)}
                      className="text-[11px] text-white/70 hover:text-white underline underline-offset-2"
                    >
                      {teleprompterOn ? 'Hide script' : 'Show script'}
                    </button>
                    {teleprompterOn && (
                      <span className="text-[10px] text-white/40 capitalize">{scrollSpeed}</span>
                    )}
                  </div>
                ) : <span />}
                <div className="flex items-center gap-2">
                  {!isRec && (
                    <Button onClick={startRecording} size="sm" className="bg-red-600 hover:bg-red-700 text-white gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-white" />
                      Record
                    </Button>
                  )}
                  {isRec && (
                    <Button onClick={stopRecording} size="sm" variant="secondary" className="gap-1.5">
                      <Square className="w-3.5 h-3.5" />
                      Stop
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Background picker */}
          <BgPicker />

          {/* Script panel — shown in preview mode only */}
          {!isRec && hasScript && (
            <div className="bg-slate-900 border-t border-white/10 px-4 py-3 space-y-3">
              {scriptDismissed ? (
                <div className="flex items-center justify-between">
                  <p className="text-[11px] text-white/40">Teleprompter off — recording without script overlay.</p>
                  <button type="button" onClick={() => setScriptDismissed(false)} className="text-[11px] text-white/60 hover:text-white underline underline-offset-2">Turn on</button>
                </div>
              ) : (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider">Your script</p>
                      <button type="button" onClick={() => setScriptDismissed(true)} className="text-[10px] text-white/40 hover:text-white/70 underline underline-offset-2">Don't use teleprompter</button>
                    </div>
                    <div className="space-y-3">
                      {scriptParas.map((para, i) => (
                        <p key={i} className="text-sm text-white/90 leading-relaxed border-l-2 border-white/20 pl-3">
                          {para}
                        </p>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-1 border-t border-white/10">
                    <p className="text-[11px] text-white/50">Scroll speed while recording</p>
                    <div className="flex items-center gap-1">
                      {(['slow', 'normal', 'fast'] as const).map(speed => (
                        <button
                          key={speed} type="button"
                          onClick={() => setScrollSpeed(speed)}
                          className={`px-2.5 py-1 rounded text-[11px] font-semibold capitalize transition-colors ${
                            scrollSpeed === speed ? 'bg-white text-slate-900' : 'text-white/50 hover:text-white/80'
                          }`}
                        >
                          {speed}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Reviewing ───────────────────────────────────────────────────────────────
  if (state === 'reviewing' && recordedUrl) {
    return (
      <div className={className}>
        <div
          className="rounded-xl overflow-hidden border border-border mx-auto"
          style={{ maxWidth: 860 }}
        >
          <video
            ref={reviewRef}
            src={recordedUrl}
            controls playsInline
            className="w-full bg-black aspect-video"
          />
        </div>
        <div
          className="flex items-center justify-between mt-3"
          style={{ maxWidth: 860, marginLeft: 'auto', marginRight: 'auto' }}
        >
          <span className="text-xs text-muted-foreground">{formatTime(elapsed)} recorded</span>
          <Button onClick={handleReRecord} variant="outline" size="sm" className="gap-1.5">
            <RotateCcw className="w-3.5 h-3.5" />
            Re-record
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
